import { trace, SpanStatusCode } from '@opentelemetry/api'
import type { Span } from '@opentelemetry/api'
import { fetcher } from 'codegen/fetcher'

const UserTokenDocument = `query UserToken { me { token } }`

let token: string | undefined = undefined
let tokenExpirationTime: number | undefined = undefined

async function fetchToken() {
  return await fetcher<{ me: { token: string } }, unknown>(UserTokenDocument)()
}

function decodeToken(token: string): { exp?: number } {
  try {
    const payload = token.split('.')[1]
    if (!payload) {
      return {}
    }
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return decoded
  } catch {
    return {}
  }
}

export async function getToken() {
  if (token && !isTokenExpired()) {
    return token
  }
  const response = await fetchToken()
  if (response?.me?.token) {
    token = response.me.token
    const decoded = decodeToken(token)
    if (decoded.exp) {
      tokenExpirationTime = decoded.exp * 1000
    } else {
      tokenExpirationTime = Date.now() + 3_600_000
    }
    return token
  }
  return undefined
}

export function getCurrentToken(): string | undefined {
  return token
}

function isTokenExpired(): boolean {
  if (!tokenExpirationTime || !token) {
    return true
  }
  const bufferMs = 30_000
  return Date.now() >= tokenExpirationTime - bufferMs
}

export function invalidateToken() {
  token = undefined
  tokenExpirationTime = undefined
}

const DEFAULT_TIMEOUT = 30_000
export async function fetchRpc<Response, Variables = unknown>({
  url,
  variables,
  headers = {},
  timeout = DEFAULT_TIMEOUT,
  controller: controllerFromCaller,
}: {
  url: string
  variables: Variables
  headers?: HeadersInit
  timeout?: number
  controller?: AbortController
}): Promise<Response> {
  const tracer = trace.getTracer('rpc-codegen')

  if (!token || isTokenExpired()) {
    await getToken()
  }

  return await tracer.startActiveSpan('fetchRpc', async span => {
    span.setAttribute('http.url', url)

    const controller = controllerFromCaller ?? getAbortController()
    const timeoutId = timeout ? setTimeout(() => controller?.abort(), timeout) : undefined

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
          ...headers,
        },
        body: JSON.stringify(variables || {}),
        signal: controller?.signal,
      })

      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        let details: unknown = undefined
        try {
          const errorBody = await response.json()
          details = errorBody?.details?.[0]?.debug
        } catch {}
        throw new FetchError(
          response.status,
          `HTTP error ${response.status}: ${response.statusText}`,
          details,
        )
      }

      const json = await response.json()

      // this can be improved once we know more of the generic error format from the services
      if ('error' in json && 'message' in json.error && 'code' in json.error) {
        const code = json.error.code || 400
        throw new ServiceError(code, json.error.message)
      }

      span.setStatus({ code: SpanStatusCode.OK })

      return json
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw handleError(span, new ServiceError(408, 'CF_ABORT_ERROR'))
      }
      throw handleError(span, error)
    } finally {
      span.end()
      clearTimeout(timeoutId)
    }
  })
}

export class FetchError extends Error {
  constructor(
    public code: number,
    message: string,
    public details?: unknown,
  ) {
    super(message)
    this.name = 'FetchError'
  }
}

export class ServiceError extends Error {
  constructor(
    public code: number,
    message: string,
  ) {
    super(message)
    this.name = 'ServiceError'
  }
}

function getAbortController() {
  try {
    return new AbortController()
  } catch {
    return undefined
  }
}

function handleError(span: Span, error: unknown) {
  span.setStatus({
    code: SpanStatusCode.ERROR,
    message: error instanceof Error ? error.message : String(error),
  })
  span.end()
  return error
}
